/**
 * listWindow — the AR-6 truncation sentinel.
 *
 * THE PROBLEM IT SOLVES. Every list query in this portal is
 * `ORDER BY <date> DESC LIMIT N`. From the client, a result of exactly N rows is
 * indistinguishable between "there were exactly N" and "there were four
 * thousand". `AuditLogPage` has capped at 200 since the day it shipped and has
 * never once said so — its search searches only those 200, and no user has ever
 * been told which 200.
 *
 * THE FIX. Ask for `N + 1`. If `N + 1` come back, the window truncated; return
 * the first N and say so. One extra row, and the cheapest possible control over
 * a class of silent wrongness (architect ruling AR-6, 2026-09-13).
 *
 * This also carries AR-1.1 condition (0): truncation is the trip-wire that
 * actually fires first, before row counts or p95, and it is the one with a live
 * detector attached — this function.
 */

export interface ListWindow<T> {
  /** At most `limit` rows. */
  rows: T[];
  /** True when the query hit its cap and older rows exist beyond it. */
  truncated: boolean;
  /** The cap that was applied, for the caption. */
  limit: number;
}

/** The row count to request: always one more than you intend to show. */
export function probeLimit(limit: number): number {
  return limit + 1;
}

/** Trim a `probeLimit()` result back to `limit` and report whether it truncated. */
export function applyWindow<T>(rows: T[], limit: number): ListWindow<T> {
  const truncated = rows.length > limit;
  return {
    rows: truncated ? rows.slice(0, limit) : rows,
    truncated,
    limit,
  };
}

/** An unbounded result, stated explicitly. Prefer this over leaving `truncated` undefined. */
export function completeWindow<T>(rows: T[], limit: number): ListWindow<T> {
  return { rows, truncated: false, limit };
}

/**
 * Inclusive SAST date bounds for a service query (AR-5 server-side date path).
 *
 * `start`/`end` are `YYYY-MM-DD` SAST day keys from `resolvePeriod()`. The
 * database stores `timestamptz`, so the day keys are widened to the SAST day's
 * real instants — `+02:00` explicitly, never a bare `Z`, which would shift the
 * boundary by two hours and reintroduce the §4.14 defect at the query layer.
 */
export function sastRangeToTimestamps(range: { start: string | null; end: string | null }): {
  since?: string;
  until?: string;
} {
  return {
    since: range.start ? `${range.start}T00:00:00+02:00` : undefined,
    until: range.end ? `${range.end}T23:59:59.999+02:00` : undefined,
  };
}
